<template>
  <b-card bg-variant="light">
    <b-form-group
      label-cols-lg="3"
      label="Boat Information"
      label-size="lg"
      label-class="font-weight-bold pt-0"
      class="mb-0"
    >
      <b-form-row>
        <b-col cols="12" md="4">
          <b-form-group label="Boat Year:" label-for="nested-year">
            <b-form-input id="nested-year" v-model="v.year__c.$model">
            </b-form-input>
            <span
              v-if="!v.year__c.integer && v.year__c.$dirty"
              class="text-danger"
              >Boat year must be a year and contain no letters
            </span>
            <span
              v-if="!v.year__c.required && v.year__c.$dirty"
              class="text-danger"
              >Boat Year is required!
            </span>
            <span
              v-if="!v.year__c.between && v.year__c.$dirty && v.year__c.integer"
              class="text-danger"
              >Boat year must be between 1900 and {{ currentYearAddOne }}.
            </span>
            <!-- Length rules, which `between` does not cover: "01900" is five
                   characters but Number("01900") is 1900, so between passes and
                   maxLength was the only failing rule, with nothing on screen. -->
            <span
              v-if="
                (!v.year__c.minLength || !v.year__c.maxLength) &&
                v.year__c.between &&
                v.year__c.integer &&
                v.year__c.$dirty
              "
              class="text-danger"
              >Boat Year must be exactly four digits.
            </span>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Boat Length:" label-for="nested-length">
            <b-form-input
              id="nested-length"
              v-model="v.length__c.$model"
            ></b-form-input>
            <span
              v-if="!v.length__c.required && v.length__c.$dirty"
              class="text-danger"
              >Boat Length is required!
            </span>
            <span
              v-if="!v.length__c.integer && v.length__c.$dirty"
              class="text-danger"
              >Boat Length in whole feet only
            </span>
            <span
              v-if="
                !v.length__c.between &&
                v.length__c.$dirty &&
                v.length__c.integer
              "
              class="text-danger"
              >Sea Tow generally accepts boats between 1' and 65'. Below or
              above that may require special authorization.
            </span>
            <!-- maxLength(2) can fail on its own: "065" is three characters
                   but Number("065") is 65, so `between` passes and this was the
                   only failing rule, with no message. -->
            <span
              v-if="
                !v.length__c.maxLength &&
                v.length__c.between &&
                v.length__c.$dirty
              "
              class="text-danger"
              >Boat Length is at most two digits - drop any leading zero.
            </span>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4">
          <b-form-group label="Boat Make:" label-for="nested-make">
            <b-form-input
              id="nested-make"
              style="text-transform: capitalize"
              v-model="v.boat_make__c.$model"
            ></b-form-input>
            <span
              v-if="!v.boat_make__c.required && v.boat_make__c.$dirty"
              class="text-danger"
              >Boat Make is required!
            </span>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col cols="12" md="6">
          <b-form-group label="Boat Color:" label-for="nested-color">
            <b-form-input
              id="nested-color"
              v-model="v.color__c.$model"
              @keypress="stripNonAlpha($event)"
            >
            </b-form-input>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Engine Type:" label-for="nested-engine">
            <b-form-select
              v-model="v.engine_type__c.$model"
              :options="EngineTypeOptions"
              class="mb-3"
            >
              <!-- 275,446 boats in Salesforce have no engine type. Without a
                     placeholder the box displayed "IO" for every one of them. -->
              <template v-slot:first>
                <b-form-select-option :value="null"
                  >-- Please select an option --</b-form-select-option
                >
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col cols="12" md="6">
          <b-form-group label="Boat Name:" label-for="nested-boat-name">
            <b-form-input
              id="nested-boat-name"
              style="text-transform: capitalize"
              v-model="v.name__c.$model"
            ></b-form-input>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group
            label="Registration/Document Number:"
            label-for="nested-doc-num"
          >
            <b-form-input
              id="nested-doc-num"
              style="text-transform: uppercase"
              v-model="v.registration_document__c.$model"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-group label="Boat is kept at:">
        <b-form-select
          v-model="v.home_port_type__c.$model"
          :options="boat_kept_at_options"
          class="mb-3"
        >
          <template v-slot:first>
            <b-form-select-option :value="null" disabled
              >-- Please select an option --</b-form-select-option
            >
          </template>
        </b-form-select>
        <span
          v-if="!v.home_port_type__c.required && v.home_port_type__c.$dirty"
          class="text-danger"
          >Boat Location is required!
        </span>
      </b-form-group>

      <b-form-row>
        <b-col>
          <b-form-group label label-for="nested-hp-desc">
            {{ marinaDesc }}
            <b-form-input
              id="nested-hp-desc"
              style="text-transform: capitalize"
              v-model="v.marina_name__c.$model"
              @blur="$emit('assign-aor')"
            ></b-form-input>
            <span
              v-if="!v.marina_name__c.required && v.marina_name__c.$dirty"
              class="text-danger"
              >Home port description is required!
            </span>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row>
        <b-col cols="12" md="6" lg="4">
          <b-form-group label="Boat City:" label-for="nested-boat-city">
            <b-form-input
              id="nested-boat-city"
              style="text-transform: capitalize"
              v-model="v.home_port_city__c.$model"
              @blur="$emit('assign-aor')"
            ></b-form-input>
            <span
              v-if="!v.home_port_city__c.required && v.home_port_city__c.$dirty"
              class="text-danger"
              >Boat City is required!
            </span>
          </b-form-group>
        </b-col>
        <b-col v-if="showBoatState" cols="12" md="6" lg="2">
          <b-form-group label="Boat State:" label-for="nested-boat-state">
            <b-form-select
              v-model="v.home_port_state__c.$model"
              :options="boat_state_options"
              class="mb-3"
              @change="$emit('assign-aor')"
              @blur="$emit('assign-aor')"
            >
              <template v-slot:first>
                <b-form-select-option :value="null" disabled
                  >-- Please select an option --</b-form-select-option
                >
              </template>
            </b-form-select>
            <span
              v-if="
                !v.home_port_state__c.required && v.home_port_state__c.$dirty
              "
              class="text-danger"
              >Boat State is required!
            </span>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6" lg="3">
          <b-form-group label="Boat Country:" label-for="nested-boat-country">
            <!-- The home port list, not the billing address list. Billing
                 offers only United States and Canada, so 246 boats sitting on
                 Other, Bahamas, Mexico, Panama or British Virgin Islands had no
                 option to match and rendered as "United States" - and a dealer
                 could not set one of those countries at all. The placeholder
                 keeps a null country from doing the same. Home port drives AOR,
                 so a change here re-runs the lookup like city and state do. -->
            <b-form-select
              v-model="v.home_port_country__c.$model"
              :options="homePortCountryOptions"
              class="mb-3"
              @change="$emit('assign-aor')"
            >
              <template v-slot:first>
                <b-form-select-option :value="null" disabled
                  >-- Please select an option --</b-form-select-option
                >
              </template>
            </b-form-select>

            <span
              v-if="
                !v.home_port_country__c.required &&
                v.home_port_country__c.$dirty
              "
              class="text-danger"
              >Boat Country is required!
            </span>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6" lg="3">
          <!-- make a non editable text field that displays this.aor__c-->
          <b-form-group label="Assigning to AOR:" label-for="nested-aor">
            <b-form-input
              id="nested-aor"
              v-model="aorFieldV.$model"
              readonly
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-form-row>
      <div>
        <b-button
          class="update-button"
          v-if="isRenew"
          :disabled="
            !this.isRenew &&
            (shippingSameAsBilling !== 'true' ||
              shippingSameAsBilling === false) &&
            this.accountSfid !== undefined
          "
          @click="$emit('update-boat')"
          type="button"
          variant="primary"
          >Update Boat Information</b-button
        >
      </div>
    </b-form-group>
  </b-card>
</template>

<script>
// Boat Information card. Inputs bind through the `v` prop (the parent's
// $v.boats Vuelidate subtree) and `aorFieldV` ($v.aor_display_field); the
// $model setters write straight back to the parent's reactive data, so
// behavior is identical to the inline markup it replaced. Boat-only option
// lists live here; the home port country list comes from the home port
// config, which is the same file that knows each country's states.
import { stripNonAlpha } from "./helpers";
import {
  homePortCountryOptions,
  homePortHasStates,
  homePortStateOptions,
  isKnownHomePortCountry,
  isValidHomePortState,
} from "@/config/homePort";

export default {
  name: "BoatInfoCard",
  props: {
    v: { type: Object, required: true },
    aorFieldV: { type: Object, required: true },
    marinaDesc: { type: String, default: "" },
    currentYearAddOne: { type: [Number, String], default: "" },
    isRenew: { type: Boolean, default: false },
    shippingSameAsBilling: { type: [Boolean, String], default: false },
    accountSfid: { type: String, default: undefined },
  },
  computed: {
    // Home_Port_State__c is a dependent picklist: Salesforce rejects a state
    // that does not belong to the chosen country. Offering only that country's
    // states is what keeps the pair valid - the flat US list this replaced
    // meant picking Canada guaranteed the boat would never sync.
    boat_state_options() {
      return homePortStateOptions(this.v.home_port_country__c.$model);
    },

    // Bahamas and Other have no states, so there is nothing to ask for. The
    // select was still rendered, offering its placeholder and nothing else
    // beside an error the dealer had no way to clear.
    showBoatState() {
      return homePortHasStates(this.v.home_port_country__c.$model);
    },
  },
  watch: {
    // Changing the country strands the state that was valid under the old one,
    // so clear it rather than submit a pair Salesforce will refuse.
    //
    // This also runs on load, because a record can arrive already holding an
    // invalid pair - production has boats with country Canada and a US state.
    // The select cannot show a value it does not offer, so without this the box
    // renders empty while the model still carries the old state, and saving
    // sends it straight back to be rejected again. Clearing makes what the
    // dealer sees and what would be submitted the same thing.
    "v.home_port_country__c.$model": {
      immediate: true,
      handler(country) {
        // A country we cannot classify is left alone; the API's normalizer
        // handles the spellings and territories this list does not carry, and
        // discarding a state on a guess would lose real data.
        if (!isKnownHomePortCountry(country)) return;

        const state = this.v.home_port_state__c.$model;
        if (state && !isValidHomePortState(country, state)) {
          this.v.home_port_state__c.$model = null;
        }
      },
    },
  },
  data() {
    return {
      homePortCountryOptions: homePortCountryOptions(),
      EngineTypeOptions: [
        { text: "IO", value: "IO" },
        { text: "Outboard", value: "Outboard" },
        { text: "Inboard", value: "Inboard" },
        { text: "Jet", value: "Jet" },
        { text: "Unspecified", value: "Unspecified" },
      ],
      boat_kept_at_options: [
        { text: "Marina", value: "Marina" },
        { text: "Home Dock", value: "Home Dock" },
        { text: "Trailer", value: "Trailer" },
        { text: "Mooring", value: "Mooring" },
        { text: "Transient", value: "Transient" },
        { text: "Other", value: "Other" },
      ],
    };
  },
  methods: {
    stripNonAlpha,
  },
};
</script>
